import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { copyToClipboard } from '@/utils/clipboard';

import { COPIED_FEEDBACK_MS, I18N_PREFIX } from './TraceIdBadge.constants';
import type { TraceIdBadgeProps } from './TraceIdBadge.types';

/**
 * The turn's trace id, copyable.
 *
 * This is the half of the round trip that did not exist: an operator could see
 * a suspicious turn in Grafana and had nowhere to paste the id, and could not
 * take one out of the console to search the logs either. The backend echoes the
 * same id in the `x-trace-id` header, so the three surfaces line up.
 */
export const TraceIdBadge = ({ traceId }: TraceIdBadgeProps) => {
	const { t } = useTranslation('translation', { keyPrefix: I18N_PREFIX });
	const [copied, setCopied] = useState(false);

	const handleClick = async () => {
		if (!(await copyToClipboard(traceId))) return;
		setCopied(true);
		window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
	};

	return (
		<button
			type="button"
			onClick={handleClick}
			aria-label={t('TRACE_COPY')}
			aria-live="polite"
			className={`inline-flex max-w-full items-center gap-3xs rounded-md border border-subtle bg-white px-2xs py-3xs font-mono text-[11px] transition-colors hover:bg-grey-25 ${
				copied ? 'text-success' : 'text-subtle'
			}`}
		>
			{copied ? <Check className="h-sm w-sm shrink-0" /> : <Copy className="h-sm w-sm shrink-0" />}
			<span className="truncate">{copied ? t('TRACE_COPIED') : traceId}</span>
		</button>
	);
};
